import styles from './ChatThreadModelSelector.module.scss';
import { useRef, useState } from 'react';
import { Button } from 'src/v2/commonComponents/Button';
import {
  SUPER_AGENT_AS_EXTERNAL_MODEL,
  SVG_SIZE_M,
  SVG_SIZE_S,
  SVG_SIZE_XL,
} from 'src/constants';
import { Icon } from 'src/components/Icon';
import {
  useBreakpoint,
  useOutsideClick,
  useUniqueId,
  useUserMetadata,
  useVisible,
} from 'src/hooks';
import { CaretDown } from '@phosphor-icons/react';
import cn from 'classnames';
import { NinjaModeSelect } from './components/NinjaModeSelect';
import { ReactTooltip } from 'src/v2/commonComponents/ReactTooltip';
import { BottomDrawer } from 'src/components/BottomDrawer';
import { OtherModelsButton } from './components/OtherModelsButton';
import { TextDivider } from 'src/v2/commonComponents/TextDivider';
import { useActiveModel } from 'src/hooks/userSettingsHooks/useActiveModel';
import { useUserSettings } from 'src/hooks';
import { SettingKeys, SingleSelectSetting } from 'src/types/settings';
import {
  getModelDisplayName,
  getModelIcon,
  isExternalModel,
  isExternalModelIcon,
} from 'src/utils';
import classNames from 'classnames';

interface ChatThreadModelSelectorProps {
  shouldOpenExternalModelsByClick?: boolean;
  isComparingMode?: boolean;
  onComparingModelChange?: (value: string | undefined) => void;
  compareModel?: string;
}

const MAX_VISIBLE_DESCRIPTION_LENGTH = 25;

export const ChatThreadModelSelector = ({
  shouldOpenExternalModelsByClick,
  isComparingMode,
  onComparingModelChange,
  compareModel,
}: ChatThreadModelSelectorProps) => {
  const { uniqueId } = useUniqueId();
  const { isMobileOrTablet } = useBreakpoint();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isVisible, onVisibilityRemove, onVisibilityToggle } = useVisible();
  const { activeModel } = useActiveModel();
  const { metadata } = useUserMetadata();

  const { otherLLMModels } = useUserSettings<SingleSelectSetting>(
    SettingKeys.WRITER_CODER_PRIMARY_MODEL,
  );

  const [isExternalModelsMenuOpened, setIsExternalModelsMenuOpened] =
    useState(false);

  const handleOutsideClick = () => {
    if (!isMobileOrTablet) {
      onVisibilityRemove();
    }
  };

  useOutsideClick(containerRef, handleOutsideClick);

  const selectedModelName = getModelDisplayName({
    targetModel: (isComparingMode ? compareModel : activeModel) as string,
    metadata: metadata,
    defaultValue: 'Ninja Mode',
  });

  const selectedModelIconName = getModelIcon({
    targetModel: (isComparingMode ? compareModel : activeModel) as string,
    metadata: metadata,
    defaultValue: 'ninjaMainIcon',
  });

  const handleCloseExternalModels = () => {
    setIsExternalModelsMenuOpened(false);
  };

  const handleOpenExternalModels = () => {
    setIsExternalModelsMenuOpened(true);
  };

  const handleMobileDrawerModelsClose = () => {
    onVisibilityRemove();
    setIsExternalModelsMenuOpened(false);
  };

  const TOOLTIP_ID = `chat-model-selector-${uniqueId}`;
  const MODELS_HOVER_TOOLTIP_ID = `chat-model-selector-hover-${uniqueId}`;

  const isExternalModelSelected = isExternalModel(activeModel);
  const isSuperAgentModel = activeModel?.includes(
    SUPER_AGENT_AS_EXTERNAL_MODEL,
  );

  const title = isExternalModelSelected
    ? 'External AI Model'
    : selectedModelName;

  const description = isExternalModelSelected
    ? selectedModelName
    : isSuperAgentModel && activeModel
      ? metadata?.[activeModel]?.description
      : 'Orchestrates using your preferred AI models';

  const icon = isExternalModelIcon(selectedModelIconName) ? (
    <Icon type={selectedModelIconName} size={SVG_SIZE_S} />
  ) : null;

  const tooltipContent = !!(
    !isVisible &&
    !isComparingMode &&
    description &&
    description?.length >= MAX_VISIBLE_DESCRIPTION_LENGTH
  )
    ? description
    : undefined;

  return (
    <div
      className={classNames(styles.root, {
        [styles.onlyTitle]: isComparingMode,
      })}
      ref={containerRef}
    >
      <Button
        color="transparent"
        onClick={onVisibilityToggle}
        tooltipId={MODELS_HOVER_TOOLTIP_ID}
        tooltipContent={tooltipContent}
        className={cn(styles.button, {
          [styles.buttonSelected]: isVisible,
          [styles.withShadow]: isComparingMode && !isMobileOrTablet,
        })}
      >
        <div className={styles.buttonContent} id={TOOLTIP_ID}>
          {isComparingMode ? (
            <>
              <Icon type={selectedModelIconName} size={SVG_SIZE_XL} />
              <span className={styles.buttonTitle}>{selectedModelName}</span>
            </>
          ) : (
            <div className={styles.buttonLabel}>
              <span className={styles.buttonTitle}>{title}</span>

              <div className={styles.buttonDescriptionWrapper}>
                {icon}
                <span className={styles.buttonDescription}>{description}</span>
              </div>
            </div>
          )}
          <CaretDown size={SVG_SIZE_M} />
        </div>
      </Button>

      {!isMobileOrTablet ? (
        <ReactTooltip
          id={TOOLTIP_ID}
          className={styles.modelsTooltip}
          isOpen={isVisible}
          place={isMobileOrTablet ? 'bottom' : 'bottom-start'}
        >
          <NinjaModeSelect
            onClose={onVisibilityRemove}
            onChange={onComparingModelChange}
            compareModel={compareModel}
          />
          <TextDivider withMargin={false} />
          <OtherModelsButton
            onClose={onVisibilityRemove}
            handleOpenExternalModels={handleOpenExternalModels}
            handleCloseExternalModels={handleCloseExternalModels}
            isExternalModelsMenuOpened={isExternalModelsMenuOpened}
            models={otherLLMModels}
            shouldOpenExternalModelsByClick={shouldOpenExternalModelsByClick}
            isComparingMode={isComparingMode}
            onComparingModelChange={onComparingModelChange}
            compareModel={compareModel}
          />
        </ReactTooltip>
      ) : (
        <BottomDrawer
          isOpen={isVisible}
          onClose={handleMobileDrawerModelsClose}
        >
          {!isExternalModelsMenuOpened && (
            <NinjaModeSelect onClose={onVisibilityRemove} />
          )}
          <TextDivider />
          <OtherModelsButton
            onClose={onVisibilityRemove}
            handleOpenExternalModels={handleOpenExternalModels}
            handleCloseExternalModels={handleCloseExternalModels}
            isExternalModelsMenuOpened={isExternalModelsMenuOpened}
            models={otherLLMModels}
            isComparingMode={isComparingMode}
            onComparingModelChange={onComparingModelChange}
            compareModel={compareModel}
          />
        </BottomDrawer>
      )}
    </div>
  );
};
